import { useState, useEffect } from "react";
import { ArrowRight, Search, CheckCircle2, AlertTriangle, FileCheck, Sparkles } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export default function Hero({ onScan }) {
  const [url, setUrl] = useState("");
  const [touched, setTouched] = useState(false);

  const isValid = url.trim().length > 3 && url.includes(".");

  const submit = (e) => {
    e.preventDefault();
    setTouched(true);
    if (!isValid) return;
    onScan(url.trim());
  };

  return (
    <section
      data-testid="hero-section"
      className="relative bg-ink-950 text-white overflow-hidden border-b border-white/5"
    >
      <div className="absolute inset-0 bg-grid-pattern opacity-40 pointer-events-none" aria-hidden="true" />
      <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-blue-600/10 blur-3xl pointer-events-none" aria-hidden="true" />

      <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-24 lg:pt-28 lg:pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Left: copy + form */}
          <div className="lg:col-span-7">
            <div
              data-testid="hero-eyebrow"
              className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] text-blue-300 uppercase mb-7"
            >
              <span className="inline-block w-6 h-px bg-blue-400/60" />
              Cumplimiento · Ley 21.719
            </div>

            <h1
              data-testid="hero-title"
              className="font-display text-5xl sm:text-6xl lg:text-7xl text-white leading-[1.02] tracking-tight"
            >
              El escáner que revisa si tu sitio cumple la{" "}
              <em className="italic text-blue-300">Ley 21.719</em>.
            </h1>

            <p
              data-testid="hero-subtitle"
              className="mt-8 text-base sm:text-lg text-slate-300 leading-relaxed max-w-2xl"
            >
              Pega la URL de tu sitio y en <strong className="text-white">30 segundos</strong> te mostramos qué te falta.
              Gratis, sin registro. <em className="text-blue-300 not-italic font-medium">No un test que tú respondes:</em> leemos tu sitio real.
            </p>

            <form onSubmit={submit} className="mt-10 max-w-xl">
              <div className="flex flex-col sm:flex-row gap-2 p-1.5 bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl focus-within:border-blue-400 transition-colors">
                <div className="flex-1 flex items-center gap-3 px-3">
                  <Search className="w-4 h-4 text-slate-400 shrink-0" />
                  <input
                    data-testid="analyze-url-input"
                    type="text"
                    inputMode="url"
                    autoComplete="url"
                    placeholder="https://tu-empresa.cl"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    onBlur={() => setTouched(true)}
                    className="flex-1 h-12 bg-transparent text-base text-white placeholder:text-slate-500 focus:outline-none"
                  />
                </div>
                <button
                  data-testid="analyze-submit-btn"
                  type="submit"
                  disabled={!isValid}
                  className="h-12 px-6 rounded-xl bg-white text-slate-900 hover:bg-slate-100 active:scale-[0.98] disabled:bg-white/30 disabled:text-white/60 disabled:cursor-not-allowed font-semibold inline-flex items-center justify-center gap-2 transition-all shrink-0"
                >
                  Escanear
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
              {touched && !isValid && (
                <p data-testid="url-error" className="mt-3 text-sm text-rose-300">
                  Ingresa una URL válida (por ejemplo, tu-empresa.cl).
                </p>
              )}
              <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-slate-400">
                <span className="inline-flex items-center gap-1.5">
                  <span className="live-dot" /> 3 análisis gratis · sin registro
                </span>
                <a href="/simulador-multas" data-testid="hero-simulador-link" className="text-blue-300 hover:text-white transition-colors">
                  ¿Solo simular multa? →
                </a>
              </div>
            </form>

            {/* trust strip */}
            <div className="mt-14 pt-8 border-t border-white/10 grid grid-cols-3 gap-4 sm:gap-8 max-w-xl">
              {[
                { k: "20.000", v: "UTM máximo en multa" },
                { k: "1 dic 2026", v: "Vigencia plena" },
                { k: "12", v: "Ítems Art. 14 ter" },
              ].map((s) => (
                <div key={s.k}>
                  <div className="font-display text-2xl sm:text-3xl text-white">{s.k}</div>
                  <div className="mt-1 text-[11px] text-slate-400 leading-snug uppercase tracking-wider font-mono">{s.v}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: live product mockup */}
          <div className="lg:col-span-5">
            <ScanMockup />
          </div>
        </div>
      </div>
    </section>
  );
}

function ScanMockup() {
  // 3 escenarios reales rotando cada 5s
  const SCENARIOS = [
    {
      url: "tu-empresa.cl",
      sid: "S-2024-11",
      score: 87,
      verdict: "ok",
      verdictLabel: "Cumple",
      summary: <>Buen score. Quedan <span className="text-amber-300">2 ajustes de bajo riesgo</span> por declarar.</>,
      signals: [
        { ok: true, label: "HTTPS" },
        { ok: true, label: "Política" },
        { ok: true, label: "Banner cookies" },
        { ok: true, label: "Canal ARCO" },
        { ok: true, label: "Contacto DPO" },
        { ok: false, label: "Catálogo (RAT)", warn: true },
      ],
      feed: [
        { t: "10:47:22", text: <>Detectados <span className="text-amber-300">3 trackers</span> en /checkout</> },
        { t: "10:47:18", text: <>Política de privacidad <span className="text-emerald-400">presente</span></> },
        { t: "10:47:11", text: <>HTTPS válido · TLS 1.3</> },
      ],
    },
    {
      url: "clinica-laguna.cl",
      sid: "S-2024-12",
      score: 42,
      verdict: "warn",
      verdictLabel: "Riesgo alto",
      summary: <>Datos de salud sin base legal documentada. <span className="text-rose-300">Multa estimada $138M–$1.4 mil M</span>.</>,
      signals: [
        { ok: true, label: "HTTPS" },
        { ok: false, label: "Política", warn: true },
        { ok: false, label: "Datos sensibles", warn: true },
        { ok: true, label: "Banner cookies" },
        { ok: false, label: "Canal ARCO", warn: true },
        { ok: false, label: "Catálogo (RAT)", warn: true },
      ],
      feed: [
        { t: "11:02:09", text: <><span className="text-rose-300">Datos sensibles</span> sin base legal explícita</> },
        { t: "11:02:04", text: <>Política con menciones genéricas, faltan 5 ítems</> },
        { t: "11:01:57", text: <>Detectado formulario médico sin checkbox</> },
      ],
    },
    {
      url: "tienda-online.cl",
      sid: "S-2024-13",
      score: 68,
      verdict: "warn",
      verdictLabel: "A revisar",
      summary: <>Pixels en checkout sin consentimiento previo. <span className="text-amber-300">Multa estimada $69M–$688M</span>.</>,
      signals: [
        { ok: true, label: "HTTPS" },
        { ok: true, label: "Política" },
        { ok: true, label: "Banner cookies" },
        { ok: false, label: "Meta Pixel", warn: true },
        { ok: false, label: "TikTok Pixel", warn: true },
        { ok: true, label: "Contacto DPO" },
      ],
      feed: [
        { t: "11:18:33", text: <><span className="text-amber-300">Meta Pixel</span> carga antes del banner en /checkout</> },
        { t: "11:18:27", text: <><span className="text-amber-300">TikTok Pixel</span> sin declaración</> },
        { t: "11:18:21", text: <>Política completa · 12/12 ítems</> },
      ],
    },
  ];

  const [i, setI] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setI((p) => (p + 1) % SCENARIOS.length), 5500);
    return () => clearInterval(id);
  }, [SCENARIOS.length]);
  const cur = SCENARIOS[i];

  const verdictStyles = {
    ok: "text-emerald-400 bg-emerald-400/10 border-emerald-400/30",
    warn: "text-amber-300 bg-amber-400/10 border-amber-400/30",
    bad: "text-rose-300 bg-rose-400/10 border-rose-400/30",
  };

  return (
    <div
      data-testid="hero-mockup"
      className="relative bg-ink-900 border border-white/10 rounded-2xl shadow-2xl shadow-blue-900/30 overflow-hidden"
    >
      {/* window chrome */}
      <div className="px-5 py-3 border-b border-white/5 flex items-center gap-3">
        <div className="flex gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-red-400/60" />
          <span className="w-2.5 h-2.5 rounded-full bg-amber-400/60" />
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-400/60" />
        </div>
        <p className="font-mono text-[11px] text-slate-500 truncate">
          leydedatoschile.com<span className="text-slate-700"> › scan ›</span>{" "}
          <AnimatePresence mode="wait">
            <motion.span
              key={cur.sid}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              {cur.sid}
            </motion.span>
          </AnimatePresence>
        </p>
        <span className="ml-auto inline-flex items-center gap-1.5 font-mono text-[10px] text-emerald-400 uppercase tracking-wider">
          <span className="live-dot" /> En vivo
        </span>
      </div>

      {/* content */}
      <div className="p-6 min-h-[520px]">
        <AnimatePresence mode="wait">
          <motion.div
            key={cur.sid}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.35 }}
          >
            <div className="flex items-center gap-2 font-mono text-[10px] text-blue-300 uppercase tracking-widest mb-3">
              <span className="text-slate-500">[ SCAN ]</span>
              {cur.url}
            </div>
            <div className="flex items-baseline gap-3">
              <motion.span
                key={`score-${cur.sid}`}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, ease: "backOut" }}
                className="font-display text-7xl text-white tabular-nums"
              >
                {cur.score}
              </motion.span>
              <span className="font-mono text-xs text-slate-500">/ 100</span>
              <span className={`ml-auto inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider border px-2 py-0.5 rounded ${verdictStyles[cur.verdict]}`}>
                {cur.verdict === "ok" ? <CheckCircle2 className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
                {cur.verdictLabel}
              </span>
            </div>
            <p className="mt-2 text-xs text-slate-400">{cur.summary}</p>

            {/* progress animado */}
            <div className="mt-5 h-1.5 bg-white/5 rounded-full overflow-hidden">
              <motion.div
                key={`bar-${cur.sid}`}
                className="h-full bg-gradient-to-r from-blue-400 via-blue-300 to-emerald-400"
                initial={{ width: 0 }}
                animate={{ width: `${cur.score}%` }}
                transition={{ duration: 0.9, ease: "easeOut" }}
              />
            </div>
            <div className="mt-2 flex justify-between text-[10px] font-mono uppercase tracking-wider text-slate-500">
              <span>0</span><span>50</span><span className="text-blue-300">{cur.score}</span><span>100</span>
            </div>

            {/* signals grid */}
            <div className="mt-6 grid grid-cols-2 gap-2">
              {cur.signals.map((s, idx) => (
                <motion.div
                  key={`${cur.sid}-${idx}`}
                  initial={{ opacity: 0, x: -4 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: 0.15 + idx * 0.04 }}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${s.warn ? "border-amber-400/30 bg-amber-400/5" : "border-white/5 bg-white/[0.02]"}`}
                >
                  {s.ok && !s.warn ? (
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                  ) : (
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-300 shrink-0" />
                  )}
                  <span className="font-mono text-[11px] text-slate-300">{s.label}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </AnimatePresence>

        {/* footer mockup */}
        <div className="mt-6 pt-4 border-t border-white/5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileCheck className="w-3.5 h-3.5 text-blue-300" />
            <span className="font-mono text-[10px] text-slate-400 uppercase tracking-widest">
              Reporte completo · PDF + acciones
            </span>
          </div>
          <Sparkles className="w-3.5 h-3.5 text-blue-300" />
        </div>
      </div>

      {/* feed at bottom */}
      <div className="border-t border-white/5 px-5 py-3 bg-ink-950/60">
        <p className="font-mono text-[9px] text-slate-600 uppercase tracking-[0.2em] mb-2">
          Actividad reciente · feed
        </p>
        <ul className="space-y-1 font-mono text-[10px]">
          <AnimatePresence mode="popLayout">
            {cur.feed.map((ev, idx) => (
              <motion.li
                key={`${cur.sid}-feed-${idx}`}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3, delay: idx * 0.08 }}
                className="flex gap-2 text-slate-500"
              >
                <span className="text-slate-600">{ev.t}</span>
                <span>▸</span>
                <span className="text-slate-300">{ev.text}</span>
              </motion.li>
            ))}
          </AnimatePresence>
        </ul>
      </div>

      {/* dots paginator */}
      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
        {SCENARIOS.map((_, idx) => (
          <button
            key={idx}
            onClick={() => setI(idx)}
            aria-label={`Mostrar escenario ${idx + 1}`}
            className={`h-1.5 rounded-full transition-all ${i === idx ? "w-5 bg-blue-300" : "w-1.5 bg-white/20 hover:bg-white/40"}`}
          />
        ))}
      </div>
    </div>
  );
}
